import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

const OrderConfirmationTable = ({
	membershipListData,
	browserResult,
	submitResult
}) => {
	const { t } = useTranslation('orderConfirmation');

	const { cardDisplayInfo } = useSelector(
		(state) => state.customerDetailsSlice
	);

	const formatDate = (date) => {
		return new Date(date || Date.now()).toLocaleDateString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric'
		});
	};

	const maskCardNumber = (lastFour) => {
		return `XXXX${lastFour || '****'}`;
	};

	const ProductInfo = () => (
		<Stack direction='row' spacing={2} alignItems='center'>
			<Box
				sx={{
					width: 75,
					height: 75,
					borderRadius: '50%',
					border: '2px solid',
					borderColor: 'divider',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
					flexShrink: 0,
					backgroundColor: 'white',
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
				}}
			>
				<Box
					component='img'
					src={membershipListData?.logo}
					alt={membershipListData?.name}
					sx={{
						maxWidth: '75%',
						maxHeight: '75%',
						objectFit: 'contain'
					}}
				/>
			</Box>
			<Box sx={{ minWidth: 0 }}>
				<Typography variant='body2' sx={{ fontWeight: 600 }}>
					{membershipListData?.name}
				</Typography>
				<Typography
					variant='caption'
					color='text.secondary'
					component='div'
					sx={{ lineHeight: 1.3 }}
				>
					{t('orderSummary:afterTrial', {
						trialDuration: browserResult?.trial_period,
						price: browserResult?.price,
						billingDuration: browserResult?.billing_duration
					})}
				</Typography>
			</Box>
		</Stack>
	);

	return (
		<Box sx={{ width: '100%' }}>
			<Stack
				direction='row'
				alignItems='flex-start'
				justifyContent='flex-start'
				spacing={{ xs: 3, sm: 0 }}
				divider={
					<Divider
						orientation='vertical'
						flexItem
						sx={{ display: { xs: 'none', sm: 'block' } }}
					/>
				}
				sx={{ mb: 3 }}
			>
				<Box sx={{ py: 1, pr: { xs: 0, sm: 2 } }}>
					<Typography variant='caption' color='text.secondary'>
						{t('date')}
					</Typography>
					<Typography variant='body2' sx={{ fontWeight: 600 }}>
						{formatDate(submitResult?.date)}
					</Typography>
				</Box>
				<Box sx={{ py: 1, px: { xs: 0, sm: 2 } }}>
					<Typography variant='caption' color='text.secondary'>
						{t('total')}
					</Typography>
					<Typography variant='body2' sx={{ fontWeight: 600 }}>
						${browserResult?.trial_price}
					</Typography>
				</Box>
				<Box sx={{ py: 1, pl: { xs: 0, sm: 2 } }}>
					<Typography variant='caption' color='text.secondary'>
						{t('paymentMethod')}
					</Typography>
					<Typography variant='body2' sx={{ fontWeight: 600 }}>
						{cardDisplayInfo?.cardType || 'VISA'}{' '}
						{maskCardNumber(cardDisplayInfo?.lastFour)}
					</Typography>
				</Box>
			</Stack>

			<Paper
				variant='outlined'
				sx={{
					borderRadius: 2,
					display: { xs: 'block', md: 'none' }
				}}
			>
				<Box sx={{ p: 2 }}>
					<ProductInfo />
				</Box>

				<Divider />

				<Stack spacing={1} sx={{ p: 2 }}>
					<Stack direction='row' justifyContent='space-between'>
						<Typography variant='body2' color='text.secondary'>
							{t('trialPrice')}
						</Typography>
						<Typography variant='body2'>
							${browserResult?.trial_price}
						</Typography>
					</Stack>
					<Stack direction='row' justifyContent='space-between'>
						<Typography variant='body2' color='text.secondary'>
							{t('quantity')}
						</Typography>
						<Typography variant='body2'>1</Typography>
					</Stack>
					<Stack direction='row' justifyContent='space-between'>
						<Typography variant='body2' color='text.secondary'>
							{t('subtotal')}
						</Typography>
						<Typography variant='body2'>
							${browserResult?.trial_price}
						</Typography>
					</Stack>
				</Stack>

				<Divider />

				<Stack direction='row' justifyContent='space-between' sx={{ p: 2 }}>
					<Typography variant='body2' sx={{ fontWeight: 600 }}>
						{t('totalPaidToday')}
					</Typography>
					<Typography variant='body2' sx={{ fontWeight: 600 }}>
						${browserResult?.trial_price}
					</Typography>
				</Stack>

				<Divider sx={{ borderColor: 'primary.main', borderWidth: 1 }} />

				<Stack
					direction='row'
					justifyContent='space-between'
					sx={{ p: 2, backgroundColor: 'grey.50' }}
				>
					<Typography
						variant='body2'
						sx={{ fontStyle: 'italic', color: 'text.secondary' }}
					>
						{t('monthlyPriceAfterTrial')}
					</Typography>
					<Typography
						variant='body2'
						sx={{ fontStyle: 'italic', color: 'text.secondary' }}
					>
						${browserResult?.price}/{t('month')}
					</Typography>
				</Stack>
			</Paper>

			<TableContainer
				component={Paper}
				variant='outlined'
				sx={{
					borderRadius: 2,
					display: { xs: 'none', md: 'block' }
				}}
			>
				<Table>
					<TableHead>
						<TableRow
							sx={{
								backgroundColor: 'grey.100',
								'& .MuiTableCell-root': {
									borderBottom: '1px solid',
									borderBottomColor: 'divider'
								}
							}}
						>
							<TableCell sx={{ fontWeight: 600, py: 1.5 }}>
								{t('product')}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600, py: 1.5 }}>
								{t('trialPrice')}
							</TableCell>
							<TableCell align='center' sx={{ fontWeight: 600, py: 1.5 }}>
								{t('quantity')}
							</TableCell>
							<TableCell align='right' sx={{ fontWeight: 600, py: 1.5 }}>
								{t('subtotal')}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell sx={{ py: 2 }}>
								<ProductInfo />
							</TableCell>
							<TableCell align='right' sx={{ py: 2 }}>
								${browserResult?.trial_price}
							</TableCell>
							<TableCell align='center' sx={{ py: 2 }}>
								1
							</TableCell>
							<TableCell align='right' sx={{ py: 2 }}>
								${browserResult?.trial_price}
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell
								colSpan={3}
								align='right'
								sx={{ py: 1.5, borderBottom: 'none' }}
							>
								<Typography variant='body2' sx={{ fontWeight: 600 }}>
									{t('totalPaidToday')}
								</Typography>
							</TableCell>
							<TableCell align='right' sx={{ py: 1.5, borderBottom: 'none' }}>
								<Typography variant='body2' sx={{ fontWeight: 600 }}>
									${browserResult?.trial_price}
								</Typography>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell
								colSpan={3}
								align='right'
								sx={{
									py: 1.5,
									borderTop: '2px solid',
									borderTopColor: 'primary.main',
									backgroundColor: 'grey.50'
								}}
							>
								<Typography
									variant='body2'
									sx={{ fontStyle: 'italic', color: 'text.secondary' }}
								>
									{t('monthlyPriceAfterTrial')}
								</Typography>
							</TableCell>
							<TableCell
								align='right'
								sx={{
									py: 1.5,
									borderTop: '2px solid',
									borderTopColor: 'primary.main',
									backgroundColor: 'grey.50'
								}}
							>
								<Typography
									variant='body2'
									sx={{ fontStyle: 'italic', color: 'text.secondary' }}
								>
									${browserResult?.price}/{t('month')}
								</Typography>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

OrderConfirmationTable.propTypes = {
	membershipListData: PropTypes.object.isRequired,
	browserResult: PropTypes.object,
	submitResult: PropTypes.object
};

export default OrderConfirmationTable;
